import { useEffect, useMemo, useState } from 'react';

import useTranslations from '@/i18n/useTranslations';
import MainPictureCard from '@/shared-components/forms/add-artwork/media/main-picture/components/MainPictureCard';
import UploadMainPicture from '@/shared-components/forms/add-artwork/media/main-picture/components/UploadMainPicture';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';
import { resolveCurrencySymbol } from '@/utils/currency.utils';

import './ArtworkAutocompletePopup.css';

const numberIsPositive = value => Number(value) > 0;

function artistName(artist) {
  return artist?.artist_name || artist?.user_name || artist?.user_username || '';
}

export default function ArtworkAutocompletePopup({
  open,
  onClose,
  artist,
  initialArtwork,
  priceFieldKey,
  currency,
  onAutocomplete,
}) {
  const t = useTranslations('common.forms.add_artwork');
  const [draft, setDraft] = useState(null);
  const [imageError, setImageError] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft({
      title: initialArtwork.artwork_title || '',
      medium: initialArtwork.artwork_medium || '',
      year: initialArtwork.artwork_year || '',
      price: initialArtwork[priceFieldKey] || '',
      size: { ...(initialArtwork.artwork_size || { unit: 'cm', length: '', height: '' }) },
      mainPicture: initialArtwork.mainPicture || null,
    });
    setImageError(null);
    setError('');
  }, [initialArtwork, open, priceFieldKey]);

  const isValid = useMemo(() => {
    if (!draft || draft.mainPicture?.loading) return false;
    return draft.title.trim().length >= 3
      && Boolean(draft.medium.trim())
      && Boolean(String(draft.year).trim())
      && Number(draft.price) > 99
      && numberIsPositive(draft.size.length)
      && numberIsPositive(draft.size.height)
      && Boolean(draft.mainPicture?.image_id);
  }, [draft]);

  const update = (key, value) => setDraft(current => ({ ...current, [key]: value }));
  const updateSize = (key, value) => setDraft(current => ({
    ...current,
    size: { ...current.size, [key]: value },
  }));

  async function submit() {
    if (!isValid || loading) return;
    setLoading(true);
    setError('');
    try {
      await onAutocomplete(draft);
      onClose();
    } catch (requestError) {
      setError(requestError?.message || t('ai_autocomplete.error'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <GenericPopup
      open={open}
      opacity
      className="generic-popup_content remove-scrollbar"
      onConfirm={submit}
      confirmButtonText={t('ai_autocomplete.submit')}
      confirmDisabled={!isValid}
      confirmLoading={loading}
    >
      <div className="generic-popup__header">
        <Text className="generic-popup__header-text-theme">{t('ai_autocomplete.title')}</Text>
        <XIcon className="generic-popup__close" onClick={loading ? null : onClose} />
      </div>
      <Text className="artwork-ai-autocomplete__intro" paragraph>
        {t('ai_autocomplete.intro')}
      </Text>
      {draft && (
        <div className="artwork-ai-autocomplete__fields">
          <Text className="artwork-ai-autocomplete__section">{t('ai_autocomplete.facts')}</Text>
          <PublicFormInput
            className="artwork-ai-autocomplete__artist"
            placeholder={t('ai_autocomplete.artist')}
            element="ai_artwork_artist"
            value={artistName(artist)}
            readOnly
          />
          <PublicFormInput
            placeholder={t('ai_autocomplete.title_field')}
            element="ai_artwork_title"
            value={draft.title}
            onChange={event => update('title', event.target.value)}
          />
          <PublicFormInput
            placeholder={t('ai_autocomplete.medium')}
            element="ai_artwork_medium"
            value={draft.medium}
            onChange={event => update('medium', event.target.value)}
          />
          <div className="artwork-ai-autocomplete__row">
            <PublicFormInput
              placeholder={t('ai_autocomplete.year')}
              element="ai_artwork_year"
              value={draft.year}
              onChange={event => update('year', event.target.value)}
            />
            <PublicFormInput
              placeholder={t('ai_autocomplete.price')}
              type="currencyInput"
              element="ai_artwork_price"
              value={draft.price}
              onChange={event => /^[0-9]*$/.test(event.target.value) && update('price', event.target.value)}
              currency={currency ? resolveCurrencySymbol(currency) : null}
            />
          </div>
          <Text className="artwork-ai-autocomplete__size-label">{t('ai_autocomplete.size')}</Text>
          <div className="artwork-ai-autocomplete__size">
            <PublicFormInput
              type="choose"
              optionA="cm"
              optionB="in"
              value={draft.size.unit}
              onChange={value => updateSize('unit', value)}
            />
            <PublicFormInput
              type="number"
              placeholder={`${t('dimensions.width')} (${draft.size.unit})`}
              value={draft.size.length}
              onChange={event => updateSize('length', event.target.value)}
            />
            <PublicFormInput
              type="number"
              placeholder={`${t('dimensions.height')} (${draft.size.unit})`}
              value={draft.size.height}
              onChange={event => updateSize('height', event.target.value)}
            />
          </div>
          <Text className="artwork-ai-autocomplete__size-label">{t('ai_autocomplete.main_picture')}</Text>
          <div className="artwork-ai-autocomplete__image">
            {draft.mainPicture ? (
              <MainPictureCard
                mainPictureFile={draft.mainPicture}
                setMainPictureFile={picture => update('mainPicture', picture)}
                previewMode={false}
              />
            ) : (
              <UploadMainPicture
                element="ai_artwork_main_picture"
                setFormError={setImageError}
                setMainPictureFile={picture => update('mainPicture', picture)}
              />
            )}
            {imageError && <Text className="artwork-ai-autocomplete__error">{t('ai_autocomplete.image_error')}</Text>}
          </div>
          <Text className="artwork-ai-autocomplete__hint" paragraph>
            {t('ai_autocomplete.generated_hint')}
          </Text>
        </div>
      )}
      {error && <Text className="artwork-ai-autocomplete__error">{error}</Text>}
    </GenericPopup>
  );
}
